import React, { useState } from "react"
import { FolderPlus, MessageSquare, Filter, Pin, Archive, Calendar, Hash } from "lucide-react"

interface Project {
  id: string
  name: string
  description?: string
  color?: string
  conversation_count?: number
  last_active_at: Date
  project_type: string
}

interface Conversation {
  id: string
  title?: string
  conversation_type: string
  primary_persona: string
  project_id: string
  todoist_task_id?: string
  created_at: Date
  updated_at: Date
  last_message_at?: Date
  message_count: number
  is_pinned: boolean
  is_archived: boolean
  tags: string[]
  project_name?: string
  project_color?: string
}

interface LeftSidebarProps {
  collapsed: boolean
  projects: Project[]
  conversations: Conversation[]
  activeProjectId: string | null
  activeConversationId: string | null
  onSelectProject: (id: string) => void
  onSelectConversation: (id: string) => void
  loading: boolean
}

type FilterType = "all" | "pinned" | "with_tools" | "tagged" | "recent"

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  collapsed,
  projects,
  conversations,
  activeProjectId,
  activeConversationId,
  onSelectProject,
  onSelectConversation,
  loading,
}) => {
  const [filter, setFilter] = useState<FilterType>("all")
  const [showNewProjectForm, setShowNewProjectForm] = useState(false)

  const getConversationIcon = (type: string) => {
    switch (type) {
      case "task-assistant":
        return "📋"
      case "debug":
        return "🔧"
      case "testing":
        return "🧪"
      default:
        return "💬"
    }
  }

  const getPersonaEmoji = (persona: string) => {
    switch (persona) {
      case "todoist":
        return "📋"
      case "planning":
        return "🧠"
      case "debug":
        return "🔧"
      default:
        return "🤖"
    }
  }

  const formatTimeAgo = (date: Date | string) => {
    const now = new Date()
    const messageDate = typeof date === "string" ? new Date(date) : date
    const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) return "Just now"
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return messageDate.toLocaleDateString()
  }

  const filteredConversations = conversations.filter((conv) => {
    switch (filter) {
      case "pinned":
        return conv.is_pinned
      case "with_tools":
        // This would need to be enhanced based on actual tool data
        return conv.conversation_type === "task-assistant"
      case "tagged":
        return conv.tags.length > 0
      case "recent": {
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        const updatedDate = typeof conv.updated_at === "string" ? new Date(conv.updated_at) : conv.updated_at
        return updatedDate > weekAgo
      }
      default:
        return true
    }
  })

  if (collapsed) {
    return (
      <div className="h-full flex flex-col p-2 gap-2">
        <button
          onClick={() => setShowNewProjectForm(true)}
          className="p-3 hover:bg-muted rounded-md transition-colors"
          title="New Project"
        >
          <FolderPlus className="h-5 w-5" />
        </button>

        <div className="flex-1 overflow-y-auto space-y-1">
          {projects.map((project) => (
            <button
              key={project.id}
              onClick={() => onSelectProject(project.id)}
              className={`w-full p-3 rounded-md transition-colors ${
                activeProjectId === project.id ? "bg-primary text-white" : "hover:bg-muted"
              }`}
              title={project.name}
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: project.color || "#6366f1" }}
              >
                {project.name?.charAt(0)}
              </div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Projects Section */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Projects</h2>
          <button
            onClick={() => setShowNewProjectForm(true)}
            className="p-1 hover:bg-muted rounded transition-colors"
            title="New Project"
          >
            <FolderPlus className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1">
          {projects.map((project) => (
            <button
              key={project.id}
              onClick={() => onSelectProject(project.id)}
              className={`w-full p-3 rounded-lg transition-colors text-left ${
                activeProjectId === project.id ? "bg-primary text-white" : "hover:bg-muted"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{ backgroundColor: project.color || "#6366f1" }}
                >
                  {project.name?.charAt(0) || "P"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{project.name}</div>
                  <div className="text-xs opacity-70">{project.conversation_count || 0} conversations</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Conversations Section */}
      <div className="flex-1 flex flex-col">
        <div className="p-4 pb-2 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Conversations</h2>
            <button className="p-1 hover:bg-muted rounded transition-colors" title="Filter conversations">
              <Filter className="h-4 w-4" />
            </button>
          </div>

          {/* Quick Filters */}
          <div className="flex gap-1 mb-2">
            {(["all", "pinned", "with_tools", "tagged", "recent"] as FilterType[]).map((filterType) => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  filter === filterType ? "bg-primary text-white" : "hover:bg-muted"
                }`}
              >
                {filterType === "with_tools" && "🛠️"}
                {filterType === "pinned" && "📌"}
                {filterType === "tagged" && "🏷️"}
                {filterType === "recent" && "⏰"}
                {filterType === "all" && "📋"}
                <span className="ml-1 capitalize">{filterType.replace("_", " ")}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-3 rounded-lg bg-muted animate-pulse">
                  <div className="h-4 bg-muted-foreground/20 rounded mb-2"></div>
                  <div className="h-3 bg-muted-foreground/20 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation.id)}
                  className={`w-full p-3 rounded-lg transition-colors text-left hover:bg-muted ${
                    activeConversationId === conversation.id ? "bg-secondary border border-primary/20" : ""
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-sm">{getConversationIcon(conversation.conversation_type)}</span>
                      <span className="text-xs">{getPersonaEmoji(conversation.primary_persona)}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm truncate">
                          {conversation.title || `${conversation.conversation_type} Chat`}
                        </div>
                        {conversation.is_pinned && <Pin className="h-3 w-3 text-primary ml-2" />}
                      </div>

                      <div className="text-xs text-muted-foreground mt-1">
                        {conversation.message_count} messages •{" "}
                        {formatTimeAgo(conversation.last_message_at || conversation.updated_at)}
                      </div>

                      {/* Tags */}
                      {conversation.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {conversation.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-muted rounded text-xs"
                            >
                              <Hash className="h-2 w-2" />
                              {tag}
                            </span>
                          ))}
                          {conversation.tags.length > 2 && (
                            <span className="text-xs text-muted-foreground">+{conversation.tags.length - 2}</span>
                          )}
                        </div>
                      )}

                      {/* Todoist Task Link */}
                      {conversation.todoist_task_id && (
                        <div className="text-xs text-primary mt-1 flex items-center gap-1">📋 Linked to task</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {filteredConversations.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <div className="text-sm">No conversations found</div>
                  <div className="text-xs mt-1">
                    {filter === "all"
                      ? "Start a new conversation to get started"
                      : `No conversations match the "${filter}" filter`}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New Project Form Modal */}
      {showNewProjectForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-background p-6 rounded-lg border border-border w-96">
            <h3 className="text-lg font-semibold mb-4">Create New Project</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Project Name</label>
                <input
                  type="text"
                  className="w-full mt-1 p-2 border border-border rounded"
                  placeholder="Enter project name..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description (optional)</label>
                <textarea
                  className="w-full mt-1 p-2 border border-border rounded resize-none"
                  rows={3}
                  placeholder="Brief description..."
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowNewProjectForm(false)}
                  className="px-4 py-2 text-sm hover:bg-muted rounded transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary/90 transition-colors">
                  Create Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
